import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Group,
  Loader,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Tabs,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { BarChart, LineChart } from '@mantine/charts';
import { notifications } from '@mantine/notifications';
import { IconDownload } from '@tabler/icons-react';
import { SavedReportsBar } from './SavedReportsBar';
import { PageHeader } from '../../components/PageHeader';
import { useBranches } from '../branches/branchesApi';
import {
  downloadInventoryExcel,
  downloadReportExcel,
  useLowStock,
  useMenuEngineering,
  useSalesByBranch,
  useSalesByCashier,
  useSalesByCategory,
  useSalesByDay,
  useSalesByHour,
  useSalesByItem,
  useSalesByPaymentMethod,
  useWastageByItem,
} from './reportsApi';
import type { MenuEngineeringClassification, ReportFilter } from './types';

// Reports v1 — read-only dashboards over the Cashier-synced order data.
// Filters live at the top (date range + optional branch); each tab below
// drives a chart or table from a single TanStack Query call.

export function ReportsPage() {
  const { t } = useTranslation();
  const { data: branches } = useBranches();

  // Default range: last 7 days inclusive, in local time → UTC at the edges.
  const [range, setRange] = useState<[Date | null, Date | null]>(() => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    return [sevenDaysAgo, today];
  });
  const [branchId, setBranchId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('byDay');

  const filter: ReportFilter | null = useMemo(() => {
    if (!range[0] || !range[1]) return null;
    // DST caveat: DatePickerInput returns local-midnight Dates; toISOString()
    // converts to UTC, which in Asia/Jerusalem (+02/+03 DST) shifts the
    // first 3 hours of the day into the prior UTC day. The server's
    // AT TIME ZONE round-trip handles steady-state correctly, but the
    // spring-forward Friday can drop or double an hour at the boundary —
    // surface in the pilot review if it matters. The proper fix when
    // Phase 2 introduces timezone configurability is
    // dayjs.tz('Asia/Jerusalem').startOf('day').utc().toISOString().
    return {
      fromUtc: new Date(range[0].getTime()).toISOString(),
      toUtc: new Date(range[1].getTime() + 24 * 60 * 60 * 1000).toISOString(),
      branchId,
    };
  }, [range, branchId]);

  return (
    <Stack gap="md">
      <PageHeader title={t('nav.items.reports')} />

      <Paper p="md" withBorder>
        <Group align="end" gap="sm" wrap="wrap">
          <DatePickerInput
            type="range"
            label={t('reports.dateRange')}
            value={range}
            onChange={(value) => setRange(value as [Date | null, Date | null])}
            valueFormat="YYYY-MM-DD"
            placeholder={t('reports.pickDates') ?? ''}
            w={280}
            allowSingleDateInRange
          />
          <Select
            label={t('reports.branch')}
            placeholder={t('reports.allBranches') ?? ''}
            data={
              branches?.map((b) => ({ value: b.branchId, label: b.nameAr })) ?? []
            }
            value={branchId}
            onChange={setBranchId}
            clearable
            w={220}
          />
        </Group>
      </Paper>

      <SavedReportsBar
        current={{ tab: activeTab, branchId, range }}
        onApply={(def) => {
          setActiveTab(def.tab);
          setBranchId(def.branchId);
          setRange([new Date(def.fromMs), new Date(def.toMs)]);
        }}
      />

      {filter ? (
        <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? 'byDay')}>
          <Tabs.List>
            <Tabs.Tab value="byDay">{t('reports.tabs.byDay')}</Tabs.Tab>
            <Tabs.Tab value="byItem">{t('reports.tabs.byItem')}</Tabs.Tab>
            <Tabs.Tab value="byCategory">{t('reports.tabs.byCategory')}</Tabs.Tab>
            <Tabs.Tab value="byCashier">{t('reports.tabs.byCashier')}</Tabs.Tab>
            <Tabs.Tab value="byHour">{t('reports.tabs.byHour')}</Tabs.Tab>
            <Tabs.Tab value="byPayment">{t('reports.tabs.byPayment')}</Tabs.Tab>
            <Tabs.Tab value="byBranch">{t('reports.tabs.byBranch')}</Tabs.Tab>
            <Tabs.Tab value="menuEngineering">{t('reports.tabs.menuEngineering')}</Tabs.Tab>
            <Tabs.Tab value="lowStock">{t('reports.tabs.lowStock')}</Tabs.Tab>
            <Tabs.Tab value="wastage">{t('reports.tabs.wastage')}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="byDay" pt="md">
            <SalesByDayPanel filter={filter} />
          </Tabs.Panel>
          <Tabs.Panel value="byItem" pt="md">
            <SalesByItemPanel filter={filter} />
          </Tabs.Panel>
          <Tabs.Panel value="byCategory" pt="md">
            <SalesByCategoryPanel filter={filter} />
          </Tabs.Panel>
          <Tabs.Panel value="byCashier" pt="md">
            <SalesByCashierPanel filter={filter} />
          </Tabs.Panel>
          <Tabs.Panel value="byHour" pt="md">
            <SalesByHourPanel filter={filter} />
          </Tabs.Panel>
          <Tabs.Panel value="byPayment" pt="md">
            <SalesByPaymentPanel filter={filter} />
          </Tabs.Panel>
          <Tabs.Panel value="byBranch" pt="md">
            <SalesByBranchPanel filter={filter} />
          </Tabs.Panel>
          <Tabs.Panel value="menuEngineering" pt="md">
            <MenuEngineeringPanel filter={filter} />
          </Tabs.Panel>
          <Tabs.Panel value="lowStock" pt="md">
            <LowStockPanel branchId={branchId} />
          </Tabs.Panel>
          <Tabs.Panel value="wastage" pt="md">
            <WastageByItemPanel branchId={branchId} filter={filter} />
          </Tabs.Panel>
        </Tabs>
      ) : (
        <Alert color="gray">{t('reports.selectRangeHint')}</Alert>
      )}
    </Stack>
  );
}

// ---- Panels ----

function SalesByDayPanel({ filter }: { filter: ReportFilter }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useSalesByDay(filter);

  return (
    <PanelShell
      isLoading={isLoading}
      error={error}
      empty={!data || data.length === 0}
      onExport={() => exportReport('sales-by-day', filter, t)}
    >
      {data && data.length > 0 && (
        <Stack gap="md">
          <Paper p="md" withBorder>
            <LineChart
              h={280}
              data={data.map((r) => ({ date: r.localDate, revenue: r.revenue }))}
              dataKey="date"
              series={[{ name: 'revenue', label: t('reports.columns.revenue'), color: 'wheels.6' }]}
              curveType="linear"
              withDots
              valueFormatter={formatMoney}
            />
          </Paper>
          <SimpleTable
            columns={[
              t('reports.columns.date'),
              t('reports.columns.orderCount'),
              t('reports.columns.revenue'),
            ]}
            rows={data.map((r) => [r.localDate, String(r.orderCount), formatMoney(r.revenue)])}
          />
        </Stack>
      )}
    </PanelShell>
  );
}

function SalesByItemPanel({ filter }: { filter: ReportFilter }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useSalesByItem(filter, 25);

  return (
    <PanelShell
      isLoading={isLoading}
      error={error}
      empty={!data || data.length === 0}
      onExport={() => exportReport('sales-by-item', filter, t, { limit: '500' })}
    >
      {data && data.length > 0 && (
        <Stack gap="md">
          <Paper p="md" withBorder>
            <BarChart
              h={320}
              data={data.slice(0, 10).map((r) => ({ name: r.itemNameSnapshot, revenue: r.revenue }))}
              dataKey="name"
              series={[{ name: 'revenue', label: t('reports.columns.revenue'), color: 'wheels.6' }]}
              valueFormatter={formatMoney}
            />
          </Paper>
          <SimpleTable
            columns={[
              t('reports.columns.item'),
              t('reports.columns.quantity'),
              t('reports.columns.revenue'),
            ]}
            rows={data.map((r) => [r.itemNameSnapshot, formatNumber(r.quantitySold), formatMoney(r.revenue)])}
          />
        </Stack>
      )}
    </PanelShell>
  );
}

function SalesByCategoryPanel({ filter }: { filter: ReportFilter }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useSalesByCategory(filter);

  return (
    <PanelShell
      isLoading={isLoading}
      error={error}
      empty={!data || data.length === 0}
      onExport={() => exportReport('sales-by-category', filter, t)}
    >
      {data && data.length > 0 && (
        <SimpleTable
          columns={[
            t('reports.columns.category'),
            t('reports.columns.orderCount'),
            t('reports.columns.revenue'),
          ]}
          rows={data.map((r) => [r.categoryNameAr, String(r.orderCount), formatMoney(r.revenue)])}
        />
      )}
    </PanelShell>
  );
}

function SalesByCashierPanel({ filter }: { filter: ReportFilter }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useSalesByCashier(filter);

  return (
    <PanelShell
      isLoading={isLoading}
      error={error}
      empty={!data || data.length === 0}
      onExport={() => exportReport('sales-by-cashier', filter, t)}
    >
      {data && data.length > 0 && (
        <SimpleTable
          columns={[
            t('reports.columns.cashier'),
            t('reports.columns.orderCount'),
            t('reports.columns.revenue'),
          ]}
          rows={data.map((r) => [r.cashierFullNameAr, String(r.orderCount), formatMoney(r.revenue)])}
        />
      )}
    </PanelShell>
  );
}

function SalesByHourPanel({ filter }: { filter: ReportFilter }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useSalesByHour(filter);

  return (
    <PanelShell
      isLoading={isLoading}
      error={error}
      empty={!data || data.length === 0}
      onExport={() => exportReport('sales-by-hour', filter, t)}
    >
      {data && data.length > 0 && (
        <Paper p="md" withBorder>
          <BarChart
            h={280}
            data={Array.from({ length: 24 }, (_, hour) => {
              const row = data.find((r) => r.hourOfDay === hour);
              return { hour: `${String(hour).padStart(2, '0')}:00`, revenue: row?.revenue ?? 0 };
            })}
            dataKey="hour"
            series={[{ name: 'revenue', label: t('reports.columns.revenue'), color: 'wheels.6' }]}
            valueFormatter={formatMoney}
          />
        </Paper>
      )}
    </PanelShell>
  );
}

function SalesByPaymentPanel({ filter }: { filter: ReportFilter }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useSalesByPaymentMethod(filter);

  return (
    <PanelShell
      isLoading={isLoading}
      error={error}
      empty={!data || data.length === 0}
      onExport={() => exportReport('sales-by-payment-method', filter, t)}
    >
      {data && data.length > 0 && (
        <SimpleTable
          columns={[
            t('reports.columns.paymentMethod'),
            t('reports.columns.transactionCount'),
            t('reports.columns.amount'),
          ]}
          rows={data.map((r) => [
            r.paymentMethodCodeSnapshot,
            String(r.transactionCount),
            formatMoney(r.totalAmount),
          ])}
        />
      )}
    </PanelShell>
  );
}

// ---- Phase 6-Reports panels ----

function SalesByBranchPanel({ filter }: { filter: ReportFilter }) {
  const { t } = useTranslation();
  // Branch rollup is tenant-wide by definition; the head-office wants
  // every branch on one page, so we strip the page-level branchId before
  // querying. Drives the chain-vs-chain comparison.
  const rolledUp = useMemo<ReportFilter>(
    () => ({ ...filter, branchId: null }),
    [filter],
  );
  const { data, isLoading, error } = useSalesByBranch(rolledUp);

  return (
    <PanelShell
      isLoading={isLoading}
      error={error}
      empty={!data || data.length === 0}
      onExport={() => exportReport('sales-by-branch', rolledUp, t)}
    >
      {data && data.length > 0 && (
        <Stack gap="md">
          <Paper p="md" withBorder>
            <BarChart
              h={280}
              data={data.map((r) => ({ name: r.branchNameAr, revenue: r.revenue }))}
              dataKey="name"
              series={[{ name: 'revenue', label: t('reports.columns.revenue'), color: 'wheels.6' }]}
              valueFormatter={formatMoney}
            />
          </Paper>
          <SimpleTable
            columns={[
              t('reports.columns.code'),
              t('reports.columns.branch'),
              t('reports.columns.orderCount'),
              t('reports.columns.revenue'),
              t('reports.columns.averageTicket'),
            ]}
            rows={data.map((r) => [
              r.branchCode,
              r.branchNameAr,
              String(r.orderCount),
              formatMoney(r.revenue),
              formatMoney(r.averageTicket),
            ])}
          />
        </Stack>
      )}
    </PanelShell>
  );
}

function MenuEngineeringPanel({ filter }: { filter: ReportFilter }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useMenuEngineering(filter);

  return (
    <PanelShell
      isLoading={isLoading}
      error={error}
      empty={!data || data.length === 0}
      onExport={() => exportReport('menu-engineering', filter, t)}
    >
      {data && data.length > 0 && (
        <SimpleTable
          columns={[
            t('reports.columns.sku'),
            t('reports.columns.item'),
            t('reports.columns.quantity'),
            t('reports.columns.revenue'),
            t('reports.columns.unitCost'),
            t('reports.columns.cogs'),
            t('reports.columns.grossProfit'),
            t('reports.columns.grossMargin'),
            t('reports.columns.classification'),
          ]}
          rows={data.map((r) => [
            r.itemSku,
            r.itemNameAr,
            formatNumber(r.quantitySold),
            formatMoney(r.revenue),
            r.unitCost === null ? '—' : formatMoney(r.unitCost),
            r.cogs === null ? '—' : formatMoney(r.cogs),
            r.grossProfit === null ? '—' : formatMoney(r.grossProfit),
            r.grossMarginPercent === null ? '—' : `${r.grossMarginPercent.toFixed(2)}%`,
            t(classificationKey(r.classification)),
          ])}
        />
      )}
    </PanelShell>
  );
}

function classificationKey(c: MenuEngineeringClassification): string {
  switch (c) {
    case 'STAR': return 'reports.menuEngineering.classification.star';
    case 'PLOWHORSE': return 'reports.menuEngineering.classification.plowhorse';
    case 'PUZZLE': return 'reports.menuEngineering.classification.puzzle';
    case 'DOG': return 'reports.menuEngineering.classification.dog';
    case 'UNCLASSIFIED': return 'reports.menuEngineering.classification.unclassified';
  }
}

// ---- Inventory report panels (Phase 2E) ----
// Both panels require a specific branch — these reports aren't
// aggregated across all branches (a "low stock across the chain" view
// is conceptually different from per-branch).

function LowStockPanel({ branchId }: { branchId: string | null }) {
  const { t } = useTranslation();
  const [threshold, setThreshold] = useState<number | ''>(5);
  const numericThreshold = typeof threshold === 'number' ? threshold : 5;
  const { data, isLoading, error } = useLowStock(
    branchId,
    numericThreshold,
    branchId !== null,
  );

  if (!branchId) {
    return <Alert color="gray">{t('reports.inventory.pickBranchHint')}</Alert>;
  }

  return (
    <Stack gap="md">
      <Group>
        <NumberInput
          label={t('reports.inventory.threshold')}
          value={threshold}
          onChange={(v) => setThreshold(typeof v === 'string' ? Number(v) : v)}
          min={0}
          decimalScale={3}
          w={180}
        />
      </Group>
      <PanelShell
        isLoading={isLoading}
        error={error}
        empty={!data || data.length === 0}
        onExport={() =>
          downloadInventoryExcel('low-stock', {
            branchId,
            threshold: String(numericThreshold),
          }).catch((err) =>
            notifications.show({
              color: 'red',
              title: t('reports.errors.exportFailed'),
              message: err instanceof Error ? err.message : 'خطأ',
            }),
          )
        }
      >
        {data && data.length > 0 && (
          <SimpleTable
            columns={[
              t('reports.inventory.columns.sku'),
              t('reports.inventory.columns.item'),
              t('reports.inventory.columns.onHand'),
            ]}
            rows={data.map((r) => [r.itemSku, r.itemNameAr, formatNumber(r.onHand)])}
          />
        )}
      </PanelShell>
    </Stack>
  );
}

function WastageByItemPanel({
  branchId,
  filter,
}: {
  branchId: string | null;
  filter: ReportFilter;
}) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useWastageByItem(
    branchId,
    filter,
    branchId !== null,
  );

  if (!branchId) {
    return <Alert color="gray">{t('reports.inventory.pickBranchHint')}</Alert>;
  }

  return (
    <PanelShell
      isLoading={isLoading}
      error={error}
      empty={!data || data.length === 0}
      onExport={() =>
        downloadInventoryExcel('wastage-by-item', {
          branchId,
          fromUtc: filter.fromUtc,
          toUtc: filter.toUtc,
        }).catch((err) =>
          notifications.show({
            color: 'red',
            title: t('reports.errors.exportFailed'),
            message: err instanceof Error ? err.message : 'خطأ',
          }),
        )
      }
    >
      {data && data.length > 0 && (
        <SimpleTable
          columns={[
            t('reports.inventory.columns.sku'),
            t('reports.inventory.columns.item'),
            t('reports.inventory.columns.totalQuantity'),
            t('reports.inventory.columns.events'),
          ]}
          rows={data.map((r) => [
            r.itemSku,
            r.itemNameAr,
            formatNumber(r.totalQuantity),
            String(r.events),
          ])}
        />
      )}
    </PanelShell>
  );
}

// ---- Helpers ----

interface PanelShellProps {
  isLoading: boolean;
  error: unknown;
  empty: boolean;
  onExport: () => void;
  children?: React.ReactNode;
}

function PanelShell({ isLoading, error, empty, onExport, children }: PanelShellProps) {
  const { t } = useTranslation();
  return (
    <Stack gap="md">
      <Group justify="flex-end">
        <Button
          variant="default"
          leftSection={<IconDownload size={16} stroke={1.6} />}
          onClick={onExport}
          disabled={isLoading || empty}
        >
          {t('reports.exportExcel')}
        </Button>
      </Group>
      {isLoading && (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      )}
      {error != null && (
        <Alert color="red">
          {error instanceof Error ? error.message : t('reports.errors.loadFailed')}
        </Alert>
      )}
      {!isLoading && !error && empty && (
        <Alert color="gray">{t('reports.empty')}</Alert>
      )}
      {!isLoading && !error && !empty && children}
    </Stack>
  );
}

function SimpleTable({ columns, rows }: { columns: string[]; rows: string[][] }) {
  return (
    <Paper withBorder>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            {columns.map((c) => (
              <Table.Th key={c}>{c}</Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, ri) => (
            <Table.Tr key={ri}>
              {row.map((cell, ci) => (
                <Table.Td key={ci} className={ci > 0 ? 'font-numeric' : undefined}>
                  {cell}
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Paper>
  );
}

async function exportReport(
  slug: string,
  filter: ReportFilter,
  t: (key: string) => string,
  extra: Record<string, string> = {},
) {
  try {
    await downloadReportExcel(slug, filter, extra);
  } catch (err) {
    notifications.show({
      color: 'red',
      title: t('reports.errors.exportFailed'),
      message: err instanceof Error ? err.message : 'خطأ غير متوقع.',
    });
  }
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'ILS',
    minimumFractionDigits: 2,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(value);
}
